import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BuildingContext } from "../contexts/BuildingContext";
import { RoomContext } from "../contexts/RoomContext";
import "./BuildingDetail.css";

import RoomRegistrationModal from "./modals/RoomRegistrationModal";
import EditBuildingModal from "./modals/EditBuildingModal";
import EditRoomModal from "./modals/EditRoomModal";
import DeleteConfirmationModal from "./modals/DeleteConfirmationModal";
import LogoutConfirmationModal from "./modals/LogoutConfirmationModal";

import { Sidebar, LoadingPage } from "../common";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./ToastStyles.css";

const BACK_API_BASE_URL = process.env.REACT_APP_BACK_API_BASE_URL;
// console.log(sessionStorage.getItem("token"))

const BuildingDetail = () => {
  const { id } = useParams();
  const { deleteBuilding, updateBuilding, fetchBuildingDetails } =
    useContext(BuildingContext);
  const { rooms, fetchRooms, addRoom, updateRoom, deleteRoom } =
    useContext(RoomContext);
  const navigate = useNavigate();

  const [buildingInfo, setBuildingInfo] = useState({});
  const [floorInfoList, setFloorInfoList] = useState([]);
  const [roomInfoList, setRoomInfoList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [role, setRole] = useState(null);
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");

  const [isRoomModalOpen, setIsRoomModalOpen] = useState(false);
  const [isEditRoomModalOpen, setIsEditRoomModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFloorInfoVisible, setIsFloorInfoVisible] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  const roomStateImages = {
    EMPTY: "/emptyIcon.png",
    CONTRACT: "/contractIcon.png",
    COMMON: "/publicIcon.png",
  };
  const roomStatusLabels = {
    EMPTY: "공실",
    CONTRACT: "입실",
    COMMON: "공용공간",
  };

  useEffect(() => {
    const fetchBuildingDetails = async () => {
      try {
        const response = await fetch(
          `${BACK_API_BASE_URL}/api/building/load/${id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: sessionStorage.getItem("token"),
            },
            method: "GET",
          }
        );

        if (response.ok) {
          const data = await response.json();

          setBuildingInfo(data.buildingInfo);
          setFloorInfoList(data.floorInfoList);
          setRoomInfoList(data.roomInfoList);
          // console.log(data)
        } else {
          console.error(
            "Failed to fetch building details:",
            response.statusText
          );
        }
      } catch (error) {
        console.error("Error fetching building details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBuildingDetails();
    fetchRooms(id);
  }, [id]);

  useEffect(() => {
    const fetchUserAuth = async () => {
      const token = sessionStorage.getItem("token");

      if (token) {
        try {
          const response = await fetch(
            `${BACK_API_BASE_URL}/api/auth/userInfo`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: sessionStorage.getItem("token"),
              },
            }
          );
          if (response.ok) {
            const result = await response.json();
            setRole(result.role);
            setUserName(result.userName);
            setUserEmail(result.email);
          } else {
            console.error("Failed to fetch user auth:", response.statusText);
            setRole("GUEST");
          }
        } catch (error) {
          console.error("Error fetching user auth:", error);
          setRole("GUEST");
        }
      } else {
        setRole("GUEST");
      }
    };

    fetchUserAuth();
  }, []);

  const handleEditBuilding = (updatedBuilding) => {
    const buildingId = id;
    // console.log("건물정보수정함수 호출, 건물아이디:",buildingId)

    const updatedBuildingInfo = {
      ...buildingInfo,
      ...updatedBuilding,
      id: buildingId,
    };
    setBuildingInfo(updatedBuildingInfo);
    updateBuilding(updatedBuildingInfo);

    // console.log("수정완 지상", updatedBuildingInfo.buildingGroundFloor)
    // console.log("수정완 지하:",updatedBuildingInfo.buildingBasementFloor)

    toast.success("건물정보를 수정하였습니다.", {
      className: "custom-toast",
      bodyClassName: "custom-toast-body",
      closeButton: false,
      icon: <img src="/toastIcon.png" alt="Toast Icon" />,
    });
  };

  const handleDelete = async () => {
    try {
      // console.log(`Deleting building with ID: ${id}`);
      await deleteBuilding(id);
      navigate("/building-list");
    } catch (error) {
      console.error("Failed to delete building:", error);
    }
  };

  const handleRoomRegister = async (newRoom) => {
    await addRoom(newRoom);
    setIsRoomModalOpen(false);
    await fetchBuildingDetails();
  };

  const handleRoomUpdate = async (updatedRoom) => {
    await updateRoom(updatedRoom.id, updatedRoom);
    setRoomInfoList((prevRooms) =>
      prevRooms.map((room) => (room.id === updatedRoom.id ? updatedRoom : room))
    );
    setIsEditRoomModalOpen(false);
    await fetchBuildingDetails();
  };

  const handleRoomDelete = async (roomId) => {
    try {
      await deleteRoom(roomId);
      setRoomInfoList((prevRooms) =>
        prevRooms.filter((room) => room.id !== roomId)
      );
      setIsEditRoomModalOpen(false);
      toast.success("호실이 삭제되었습니다.", {
        className: "custom-toast",
        bodyClassName: "custom-toast-body",
        closeButton: false,
        icon: <img src="/toastIcon.png" alt="Toast Icon" />,
      });
      await fetchBuildingDetails(id);
    } catch (error) {
      console.error("Failed to delete room:", error);
    }
  };

  const handleLogout = () => {
    setIsLogoutModalOpen(true);
  };

  const confirmLogout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("role");

    setIsLogoutModalOpen(false);
    navigate("/building-list");
    window.location.reload();
  };

  const handleLoginRedirect = () => {
    navigate("/login");
  };

  const handleToggleFloorInfo = () => {
    setIsFloorInfoVisible((prevState) => !prevState);
  };

  const handleEditRoomClick = (room) => {
    setSelectedRoom(room);
    setIsEditRoomModalOpen(true);
  };

  const renderFloorRows = () => {
    return floorInfoList.map((floor, index) => (
      <div key={`floor-${index}`} className="floor-row">
        <div className="floor-name">
          {floor.floorNum > 0
            ? `지상 ${floor.floorNum}`
            : `지하 ${-floor.floorNum}`}
        </div>
        <div className="floor-area">{floor.area}m²</div>
      </div>
    ));
  };

  const renderRoomRows = () => {
    return roomInfoList.map((room, index) => {
      const floorDisplay =
        room.roomFloor < 0 ? `B${Math.abs(room.roomFloor)}` : room.roomFloor;
      return (
        <div key={`room-${index}`} className="room-row" data-room-id={room.id}>
          <div className="room-row-header">
            <div className="room-row-header-1">
              <div className="room-floor-number">
                <div className="room-detail-info-input">
                  {floorDisplay}&nbsp;
                </div>
                층
              </div>
              <div className="building-name">{room.roomName}</div>
              <div className="room-row-separator"></div>
            </div>
            <button
              className="edit-room-button"
              onClick={() => handleEditRoomClick(room)}
            >
              <img
                src="/editIcon.png"
                alt="Edit Room"
                className="edit-room-icon"
              />
              수정
            </button>
          </div>
          <div className="room-specific-info-row">
            <div className="room-detail-info">
              <div className="room-state">
                <img
                  src={roomStateImages[room.roomStatus]}
                  alt={roomStatusLabels[room.roomStatus]}
                  className="room-state-icon"
                />
                <div className={`${room.roomStatus}-style`}>
                  {roomStatusLabels[room.roomStatus]}
                </div>
              </div>
              <div className="room-customer-name">
                임차사명
                <div className="room-detail-info-input">
                  {room.roomCustomerName}
                </div>
              </div>
              <div className="room-contract-area">
                계약면적
                <div className="room-detail-info-input">
                  {room.roomTotalArea}m² (
                  {(room.roomTotalArea / 3.3).toFixed(2)})평
                </div>
              </div>
              <div className="room-private-area">
                전용면적
                <div className="room-detail-info-input">
                  {room.roomPrivateArea}m² (
                  {(room.roomPrivateArea / 3.3).toFixed(2)})평
                </div>
              </div>
              <div className="room-watermeter">
                수도계량기
                <div className="room-detail-info-input">
                  {room.roomWaterMeter}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!buildingInfo) {
    return <div>건물을 찾을 수 없습니다.</div>;
  }

  return (
    <div className="building-detail-page">
      <Sidebar />
      <div className="sideBarElseContainer">
        <div className="building-detail-header">
          <div className="detail-header-left">
            <button
              className="back-to-list-button"
              onClick={() =>
                navigate(role === "ADMIN" ? "/admin" : "/building-list")
              }
            >
              <img
                src="/backToList.png"
                alt="Back to List"
                className="back-to-list-icon"
              />
            </button>
            <div className="detail-header-title">
              <div className="header-title-1">건물 상세</div>
              <div className="header-title-2">건물 정보</div>
            </div>
          </div>
          <div className="detail-user-info">
            {role === "GUEST" ? (
              <div className="lead-to-login">
                <button
                  className="login-redirect-button"
                  onClick={handleLoginRedirect}
                >
                  <img
                    src="/leadToLogin.png"
                    alt="Login Icon"
                    className="login-icon"
                  />
                  로그인 후 편하게 건물관리 해보세요
                </button>
              </div>
            ) : (
              <div className="logged-in-info-container">
                <img
                  src="/loggedinInfo.png"
                  alt="Logged In"
                  className="logged-in-icon"
                />
                <div className="logged-in-info">
                  <div className="logged-in-name">{userName}</div>
                  <div className="logged-in-email">{userEmail}</div>
                </div>
                <button className="logout-button" onClick={handleLogout}>
                  로그아웃
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="content-container">
          <div className="building-detail-container">
            <div className="detail-header-row">
              <h3>건물정보</h3>
              <button
                className="edit-detail-button"
                onClick={() => setIsEditModalOpen(true)}
              >
                <img
                  src="/editIcon.png"
                  alt="Edit Icon"
                  className="edit-icon"
                />
                건물 정보 수정
              </button>
            </div>

            <div className="detail-building-info">
              <div className="building-name">{buildingInfo.buildingName}</div>
              <div className="building-address">
                <img
                  src="/addressIcon.png"
                  alt="Address Icon"
                  className="address-icon"
                />
                {buildingInfo.address}
              </div>

              <div className="detail-specific-info-row">
                <div className="detail-specific-info-1">
                  <div className="room-num">
                    총 호실
                    <div className="specific-info-input">
                      {buildingInfo.roomTotalNumber}
                    </div>
                  </div>
                  <div className="empty-room-num">
                    공실
                    <div className="specific-info-input">
                      {buildingInfo.roomEmptyNumber}
                    </div>
                  </div>
                  <div className="floor-num">
                    지상 층 수
                    <div className="specific-info-input">
                      {buildingInfo.buildingGroundFloor}
                    </div>
                  </div>
                  <div className="underfloor-num">
                    지하 층 수
                    <div className="specific-info-input">
                      {buildingInfo.buildingBasementFloor}
                    </div>
                  </div>
                </div>
                <div className="separator"></div>
                <div className="detail-specific-info-2">
                  <div className="building-private-area">
                    전용면적
                    <div className="specific-info-input">
                      {buildingInfo.buildingPrivateArea}m² (
                      {(buildingInfo.buildingPrivateArea / 3.3).toFixed(2)})평
                    </div>
                  </div>
                  <div className="building-public-area">
                    공용면적
                    <div className="specific-info-input">
                      {buildingInfo.buildingPublicArea}m² (
                      {(buildingInfo.buildingPublicArea / 3.3).toFixed(2)})평
                    </div>
                  </div>
                  <div className="building-total-area">
                    연면적
                    <div className="specific-info-input">
                      {buildingInfo.buildingTotalArea}m² (
                      {(buildingInfo.buildingTotalArea / 3.3).toFixed(2)})평
                    </div>
                  </div>
                  <div className="building-watermeter">
                    수도 계량기
                    <div className="specific-info-input">
                      {buildingInfo.buildingWaterMeter}
                    </div>
                  </div>
                </div>
              </div>

              <div className="detail-floor-info">
                층별 정보
                <button
                  className="show-floor-info-button"
                  onClick={handleToggleFloorInfo}
                >
                  <img
                    src={
                      isFloorInfoVisible ? "/hideFloor.png" : "/showFloor.png"
                    }
                    alt="Show/Hide Floor info"
                    className="show-floor-icon"
                  />
                </button>
              </div>
              {isFloorInfoVisible && (
                <div className="floor-info-container">{renderFloorRows()}</div>
              )}
            </div>

            <div className="detail-room-info">
              <div className="detail-room-header-row">
                <h3>호실 정보</h3>
                <button
                  className="add-room-button"
                  onClick={() => setIsRoomModalOpen(true)}
                >
                  + 호실 추가
                </button>
              </div>
              <div className="room-info-container">{renderRoomRows()}</div>
            </div>

            <button
              className="delete-building-button"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              건물 삭제
            </button>

            <EditBuildingModal
              isOpen={isEditModalOpen}
              onClose={() => setIsEditModalOpen(false)}
              building={buildingInfo}
              onSave={handleEditBuilding}
              buildingId={id}
            />
            <RoomRegistrationModal
              isOpen={isRoomModalOpen}
              onClose={() => setIsRoomModalOpen(false)}
              onRegister={handleRoomRegister}
              buildingId={id}
              fetchRooms={fetchRooms}
              isEdit={false}
            />
            <EditRoomModal
              isOpen={isEditRoomModalOpen}
              onClose={() => setIsEditRoomModalOpen(false)}
              onUpdate={handleRoomUpdate}
              onDelete={handleRoomDelete}
              currentRoom={selectedRoom}
              buildingId={id}
              fetchRooms={fetchRooms}
            />
            <DeleteConfirmationModal
              isOpen={isDeleteModalOpen}
              onClose={() => setIsDeleteModalOpen(false)}
              onConfirm={handleDelete}
            />
            <LogoutConfirmationModal
              isOpen={isLogoutModalOpen}
              onClose={() => setIsLogoutModalOpen(false)}
              onConfirm={confirmLogout}
            />
          </div>
        </div>
      </div>
      <ToastContainer
        position="bottom-center"
        autoClose={2000}
        hideProgressBar
      />
    </div>
  );
};

export default BuildingDetail;
